import React, { useState, useRef, useEffect } from 'react';
import { Input } from 'antd';
import styled, { keyframes } from 'styled-components';
import { generateAiContent } from '../../../Apis/Jobs';

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

const bounce = keyframes`
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-4px); }
`;

const Fab = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #47a4ea;
  border: none;
  cursor: pointer;
  display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #fff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  z-index: 999;
  transition: transform 0.2s;
  &:hover {
    transform: scale(1.08);
  }
`;

const Panel = styled.div`
  position: fixed;
  bottom: 80px;
  right: 24px;
  width: 360px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.09);
  border-radius: 14px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.15);
  z-index: 999;
  flex-direction: column;
  overflow: hidden;
  max-height: 520px;
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: #47a4ea;
  color: #fff;
  flex-shrink: 0;
`;

const HeaderTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 7px;
`;

const PulseDot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #fff;
  opacity: 0.7;
  animation: ${pulse} 1.8s ease-in-out infinite;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 16px;
  opacity: 0.8;
  padding: 0;
  line-height: 1;
  &:hover {
    opacity: 1;
  }
`;

const Messages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 160px;
`;

const Msg = styled.div`
  max-width: 88%;
  padding: 8px 11px;
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.55;
  ${({ $role }) => {
    if ($role === 'user')
      return `
        background: #47a4ea;
        color: #fff;
        border-bottom-right-radius: 3px;
        align-self: flex-end;
      `;
    if ($role === 'ai')
      return `
        background: #f0f8ff;
        color: #1a1917;
        border-bottom-left-radius: 3px;
        align-self: flex-start;
        border: 1px solid #b3cef0;
      `;
    return `
      background: #f9f8f6;
      color: #a09e99;
      font-size: 11px;
      align-self: center;
      border: 1px solid rgba(0,0,0,0.09);
      border-radius: 8px;
      font-style: italic;
      max-width: 94%;
    `;
  }}
`;

const ConfirmCard = styled.div`
  background: #f0f8ff;
  border: 1px solid #b3cef0;
  border-radius: 10px;
  border-bottom-left-radius: 3px;
  padding: 10px 11px;
  font-size: 12px;
  align-self: flex-start;
  max-width: 94%;
`;

const ConfirmText = styled.div`
  color: #1a1917;
  margin-bottom: 8px;
  line-height: 1.5;
`;

const ConfirmBtns = styled.div`
  display: flex;
  gap: 6px;
`;

const ApplyBtn = styled.button`
  background: #47a4ea;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  &:hover {
    background: #3593d9;
  }
`;

const DiscardBtn = styled.button`
  background: #f9f8f6;
  color: #595959;
  border: 1px solid rgba(0, 0, 0, 0.09);
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 11px;
  cursor: pointer;
  font-family: inherit;
  &:hover {
    background: #f0f0f0;
  }
`;

const TypingDots = styled.div`
  align-self: flex-start;
  background: #f0f8ff;
  border: 1px solid #b3cef0;
  border-radius: 10px;
  border-bottom-left-radius: 3px;
  padding: 9px 13px;
  display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
  align-items: center;
  gap: 4px;
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #47a4ea;
  display: inline-block;
  animation: ${bounce} 0.9s ease-in-out infinite;
  &:nth-child(2) {
    animation-delay: 0.15s;
  }
  &:nth-child(3) {
    animation-delay: 0.3s;
  }
`;

const InputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  padding: 10px 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.09);
  flex-shrink: 0;
  background: #f9f8f6;
`;

const SendBtn = styled.button`
  background: #47a4ea;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  font-size: 14px;
  transition: background 0.15s;
  &:hover {
    background: #3593d9;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const INITIAL_MESSAGE = {
  id: 0,
  role: 'system',
  content:
    'Tell me what to change — I can add, edit, or remove headings, update settings, write AI prompts, and more.'
};

const extractDefaultValueText = html => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  tmp.querySelectorAll('[data-action="k2p-remove"]').forEach(el => el.remove());
  return tmp.textContent.replace(/\s+/g, ' ').trim();
};

const buildSystemPrompt = (draft, k2ps) => {
  const serializeHeading = h => ({
    type: 'HEADING',
    customUIHeading: h.customUIHeading,
    systemHeading: h.systemHeading,
    description: h.description,
    defaultValue: extractDefaultValueText(h.defaultValue),
    appendToDefault: h.appendToDefault,
    submitToEmr: h.submitToEmr,
    suppressOnEmpty: h.suppressOnEmpty,
    useAI: h.useAI,
    aiPrompt: h.aiPrompt,
    required: h.required,
    fieldType:
      h.sectionType === 'checkbox'
        ? 'CHECKBOX'
        : h.sectionType === 'droplist'
        ? 'DROPDOWN'
        : 'TEXT',
    options: (h.options || []).map(o => o.value).filter(Boolean)
  });

  const rawItems = draft?.items
    ? draft.items.map(item => {
        if (item.type === 'sectionGroup') {
          return {
            type: 'SECTION_GROUP',
            groupTitle: item.title,
            push_title_to_note: item.push_title_to_note || false,
            headings: (item.headings || []).map(serializeHeading)
          };
        }
        return serializeHeading(item);
      })
    : (draft?.sectionHeadings || []).map(serializeHeading);

  const snapshot = {
    templateName: draft?.templateName || '',
    templateDescription: draft?.templateDescription || '',
    items: rawItems
  };

  const eligibleK2ps = (k2ps || []).filter(k => !k.restrictedToTemplates);
  const k2pText = eligibleK2ps.length
    ? eligibleK2ps
        .map(
          k =>
            `key: ${k.key} | phrase: ${(k.phrase || '')
              .replace(/<[^>]*>/g, '')
              .trim()}`
        )
        .join('\n')
    : 'none';

  return `You are an AI assistant helping configure a CascadeMD clinical note template. The user will describe changes to make. Respond with ONLY a JSON object — no markdown, no prose, no explanation outside the JSON.

Current template state:
${JSON.stringify(snapshot, null, 2)}

Available K2Ps (use exact key name when setting defaultValue to a K2P):
${k2pText}

Response format:
{
  "type": "minor" | "structural",
  "summary": "one sentence describing what will change",
  "changes": [<change objects>]
}

"minor" — field updates: toggles, descriptions, AI prompts, default values, fieldType, options. Auto-applied without confirmation.
"structural" — structural changes: add or remove headings or section groups, rename headings or groups. Requires user confirmation before applying.

Template structure:
The template has an "items" array. Each item is either a standalone HEADING or a SECTION_GROUP that contains headings. Use groupTitle to identify a section group and headingName (customUIHeading) to identify a heading. Headings inside a section group are accessed via addHeadingToGroup / removeHeadingFromGroup. setField, renameHeading, and removeHeading search across ALL headings — both standalone and inside groups — so you do not need to specify the group when targeting a heading by name.

Change object formats:

Set a field on any heading (standalone or inside a group):
{"op":"setField","headingName":"exact customUIHeading value","field":"required|appendToDefault|submitToEmr|useAI|suppressOnEmpty|description|aiPrompt|defaultValue|fieldType|options","value":<boolean, string, or array of strings for options>}

Add a standalone heading:
{"op":"addHeading","afterHeading":"exact heading name to insert after, or null to append","heading":{"customUIHeading":"","systemHeading":"None","description":"","defaultValue":"","appendToDefault":false,"submitToEmr":true,"suppressOnEmpty":false,"useAI":false,"aiPrompt":"","required":false,"fieldType":"TEXT","options":[]}}

Remove a standalone heading:
{"op":"removeHeading","headingName":"exact customUIHeading value"}

Rename any heading (standalone or inside a group):
{"op":"renameHeading","headingName":"exact customUIHeading value","newName":"new name"}

Add a section group:
{"op":"addSectionGroup","groupTitle":"group name","push_title_to_note":false}

Remove a section group (removes all its headings too):
{"op":"removeSectionGroup","groupTitle":"exact group title"}

Rename a section group:
{"op":"renameSectionGroup","groupTitle":"exact group title","newTitle":"new title"}

Add a heading inside a section group:
{"op":"addHeadingToGroup","groupTitle":"exact group title","heading":{"customUIHeading":"","systemHeading":"None","description":"","defaultValue":"","appendToDefault":false,"submitToEmr":true,"suppressOnEmpty":false,"useAI":false,"aiPrompt":"","required":false,"fieldType":"TEXT","options":[]}}

Remove a heading from a section group:
{"op":"removeHeadingFromGroup","groupTitle":"exact group title","headingName":"exact customUIHeading value"}

Update template-level fields:
{"op":"setTemplateName","value":"new name"}
{"op":"setTemplateDescription","value":"new description"}

Rules:
- systemHeading must always be "None"
- Match headingName and groupTitle exactly to the values in the current template state
- Only include changes that will actually modify the current state
- fieldType rules: use "TEXT" for normal dictation sections. Use "CHECKBOX" for multi-select option lists. Use "DROPDOWN" for single-select option lists. IMPORTANT: fieldType must only be "CHECKBOX" or "DROPDOWN" when submitToEmr is false. If changing fieldType to CHECKBOX or DROPDOWN, also include a setField op to set submitToEmr to false on that heading.
- options: only relevant when fieldType is "CHECKBOX" or "DROPDOWN". Value must be an array of strings e.g. ["Option A","Option B"]. When fieldType is "TEXT", options should be [].
- useAI constraint: only ONE heading across the entire template (standalone or inside any group) may have useAI: true at a time. If enabling useAI on a heading, first check the full template state and include a setField op to disable useAI on any other heading that currently has it.
- When enabling useAI on a heading, also include a setField op to set a meaningful aiPrompt unless the user says not to.
- defaultValue — two cases:
  1. K2P reference: use the exact key string from the Available K2Ps list. Do not guess or approximate. Do not wrap in \${} or any other syntax.
  2. Plain text: use it directly.
- If the request is unclear or cannot be fulfilled: {"type":"error","summary":"brief explanation of the problem"}
- Return ONLY valid JSON. Never wrap in markdown code blocks.`;
};

const AIChatPanel = ({
  draft,
  setDraft,
  k2ps = [],
  visible,
  templateId,
  applyPillsFromText,
  headingContentRefs
}) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const msgCounter = useRef(1);
  const msgsEndRef = useRef(null);

  useEffect(() => {
    setMessages([INITIAL_MESSAGE]);
    setOpen(false);
    setInput('');
  }, [templateId]);

  useEffect(() => {
    if (msgsEndRef.current)
      msgsEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, pending]);

  const addMessage = msg => {
    const id = ++msgCounter.current;
    setMessages(prev => [...prev, { id, ...msg }]);
    return id;
  };

  const applyChanges = changes => {
    const domUpdates = [];

    const makeHeading = h => ({
      id: `h-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type: 'heading',
      customUIHeading: h?.customUIHeading || 'New Section',
      systemHeading: 'None',
      description: h?.description || '',
      defaultValue: h?.defaultValue || '',
      appendToDefault: !!h?.appendToDefault,
      submitToEmr: h?.submitToEmr !== false,
      suppressOnEmpty: !!h?.suppressOnEmpty,
      useAI: !!h?.useAI,
      aiPrompt: h?.aiPrompt || '',
      required: !!h?.required,
      sectionType:
        h?.fieldType === 'CHECKBOX'
          ? 'checkbox'
          : h?.fieldType === 'DROPDOWN'
          ? 'droplist'
          : 'dictate',
      options: (h?.options || []).map(v => ({ value: v }))
    });

    setDraft(prev => {
      // If the draft has sectionHeadings it's the flat structure (TRPA-6926).
      // If it only has items (no sectionHeadings), it's the new structure (TRPA-7326+).
      const useLegacy = prev.sectionHeadings !== undefined;

      // Legacy flat structure (sectionHeadings — pre-TRPA-7326)
      if (useLegacy) {
        let updated = {
          ...prev,
          sectionHeadings: [...(prev.sectionHeadings || [])]
        };
        changes.forEach(c => {
          if (c.op === 'setField') {
            updated.sectionHeadings = updated.sectionHeadings.map((h, i) => {
              if (h.customUIHeading !== c.headingName) return h;
              let value = c.value;
              if (c.field === 'defaultValue' && applyPillsFromText) {
                value = applyPillsFromText(c.value, k2ps) || c.value;
                domUpdates.push({ key: i, html: value });
              }
              return { ...h, [c.field]: value };
            });
          } else if (c.op === 'addHeading' || c.op === 'addHeadingToGroup') {
            const newH = makeHeading(c.heading);
            if (c.op === 'addHeading' && c.afterHeading) {
              const idx = updated.sectionHeadings.findIndex(
                h => h.customUIHeading === c.afterHeading
              );
              const insertAt =
                idx !== -1 ? idx + 1 : updated.sectionHeadings.length;
              const arr = [...updated.sectionHeadings];
              arr.splice(insertAt, 0, newH);
              updated.sectionHeadings = arr;
            } else {
              updated.sectionHeadings = [...updated.sectionHeadings, newH];
            }
          } else if (
            c.op === 'removeHeading' ||
            c.op === 'removeHeadingFromGroup'
          ) {
            updated.sectionHeadings = updated.sectionHeadings.filter(
              h => h.customUIHeading !== c.headingName
            );
          } else if (c.op === 'renameHeading') {
            updated.sectionHeadings = updated.sectionHeadings.map(h =>
              h.customUIHeading === c.headingName
                ? { ...h, customUIHeading: c.newName }
                : h
            );
          } else if (c.op === 'setTemplateName') {
            updated.templateName = c.value;
          } else if (c.op === 'setTemplateDescription') {
            updated.templateDescription = c.value;
          }
        });
        return updated;
      }

      // Items-based structure (TRPA-7326+)
      let updated = { ...prev, items: [...(prev.items || [])] };

      changes.forEach(c => {
        if (c.op === 'setField') {
          updated.items = updated.items.map(item => {
            if (item.type === 'sectionGroup') {
              return {
                ...item,
                headings: (item.headings || []).map(h => {
                  if (h.customUIHeading !== c.headingName) return h;
                  let value = c.value;
                  if (c.field === 'defaultValue' && applyPillsFromText) {
                    value = applyPillsFromText(c.value, k2ps) || c.value;
                    domUpdates.push({ key: h.id, html: value });
                  }
                  if (c.field === 'fieldType') {
                    return {
                      ...h,
                      sectionType:
                        value === 'CHECKBOX'
                          ? 'checkbox'
                          : value === 'DROPDOWN'
                          ? 'droplist'
                          : 'dictate'
                    };
                  }
                  if (c.field === 'options') {
                    return {
                      ...h,
                      options: (Array.isArray(value) ? value : []).map(v => ({
                        value: v
                      }))
                    };
                  }
                  return { ...h, [c.field]: value };
                })
              };
            }
            if (item.customUIHeading !== c.headingName) return item;
            let value = c.value;
            if (c.field === 'defaultValue' && applyPillsFromText) {
              value = applyPillsFromText(c.value, k2ps) || c.value;
              domUpdates.push({ key: item.id, html: value });
            }
            if (c.field === 'fieldType') {
              return {
                ...item,
                sectionType:
                  value === 'CHECKBOX'
                    ? 'checkbox'
                    : value === 'DROPDOWN'
                    ? 'droplist'
                    : 'dictate'
              };
            }
            if (c.field === 'options') {
              return {
                ...item,
                options: (Array.isArray(value) ? value : []).map(v => ({
                  value: v
                }))
              };
            }
            return { ...item, [c.field]: value };
          });
        } else if (c.op === 'addHeading') {
          const newH = makeHeading(c.heading);
          if (c.afterHeading) {
            const idx = updated.items.findIndex(
              i =>
                i.type !== 'sectionGroup' &&
                i.customUIHeading === c.afterHeading
            );
            const insertAt = idx !== -1 ? idx + 1 : updated.items.length;
            const arr = [...updated.items];
            arr.splice(insertAt, 0, newH);
            updated.items = arr;
          } else {
            updated.items = [...updated.items, newH];
          }
        } else if (c.op === 'removeHeading') {
          updated.items = updated.items
            .filter(
              item =>
                !(
                  item.type !== 'sectionGroup' &&
                  item.customUIHeading === c.headingName
                )
            )
            .map(item =>
              item.type === 'sectionGroup'
                ? {
                    ...item,
                    headings: (item.headings || []).filter(
                      h => h.customUIHeading !== c.headingName
                    )
                  }
                : item
            );
        } else if (c.op === 'renameHeading') {
          updated.items = updated.items.map(item => {
            if (item.type === 'sectionGroup') {
              return {
                ...item,
                headings: (item.headings || []).map(h =>
                  h.customUIHeading === c.headingName
                    ? { ...h, customUIHeading: c.newName }
                    : h
                )
              };
            }
            return item.customUIHeading === c.headingName
              ? { ...item, customUIHeading: c.newName }
              : item;
          });
        } else if (c.op === 'addSectionGroup') {
          updated.items = [
            ...updated.items,
            {
              id: `group-${Date.now()}-${Math.random()
                .toString(36)
                .slice(2, 7)}`,
              type: 'sectionGroup',
              title: c.groupTitle || 'New Group',
              push_title_to_note: !!c.push_title_to_note,
              headings: []
            }
          ];
        } else if (c.op === 'removeSectionGroup') {
          updated.items = updated.items.filter(
            item =>
              !(item.type === 'sectionGroup' && item.title === c.groupTitle)
          );
        } else if (c.op === 'renameSectionGroup') {
          updated.items = updated.items.map(item =>
            item.type === 'sectionGroup' && item.title === c.groupTitle
              ? { ...item, title: c.newTitle }
              : item
          );
        } else if (c.op === 'addHeadingToGroup') {
          const newH = makeHeading(c.heading);
          updated.items = updated.items.map(item =>
            item.type === 'sectionGroup' && item.title === c.groupTitle
              ? { ...item, headings: [...(item.headings || []), newH] }
              : item
          );
        } else if (c.op === 'removeHeadingFromGroup') {
          updated.items = updated.items.map(item =>
            item.type === 'sectionGroup' && item.title === c.groupTitle
              ? {
                  ...item,
                  headings: (item.headings || []).filter(
                    h => h.customUIHeading !== c.headingName
                  )
                }
              : item
          );
        } else if (c.op === 'setTemplateName') {
          updated.templateName = c.value;
        } else if (c.op === 'setTemplateDescription') {
          updated.templateDescription = c.value;
        }
      });

      return updated;
    });

    if (domUpdates.length && headingContentRefs) {
      setTimeout(() => {
        domUpdates.forEach(({ key, html }) => {
          const el = headingContentRefs.current?.[key];
          if (el) el.innerHTML = html;
        });
      }, 0);
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || pending) return;
    setInput('');
    addMessage({ role: 'user', content: text });
    setPending(true);

    try {
      const payload = [
        { role: 'system', content: buildSystemPrompt(draft, k2ps) },
        { role: 'user', content: text }
      ];
      const response = await generateAiContent(payload);
      const rawJson = response?.data?.result?.[0];
      if (!rawJson) throw new Error('No response received. Please try again.');

      const action = JSON.parse(rawJson.replace(/```json|```/g, '').trim());

      if (action.type === 'error') {
        addMessage({ role: 'ai', content: action.summary });
      } else if (action.type === 'minor') {
        applyChanges(action.changes);
        addMessage({ role: 'ai', content: action.summary });
      } else if (action.type === 'structural') {
        addMessage({
          role: 'confirm',
          content: action.summary,
          changes: action.changes
        });
      }
    } catch (err) {
      addMessage({
        role: 'system',
        content: err.message || 'Something went wrong. Please try again.'
      });
    } finally {
      setPending(false);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleApply = (msgId, changes) => {
    applyChanges(changes);
    setMessages(prev =>
      prev.map(m => (m.id === msgId ? { ...m, resolved: 'applied' } : m))
    );
  };

  const handleDiscard = msgId => {
    setMessages(prev =>
      prev.map(m => (m.id === msgId ? { ...m, resolved: 'discarded' } : m))
    );
  };

  return (
    <>
      <Fab
        $visible={visible}
        onClick={() => setOpen(o => !o)}
        title='AI Template Assistant'
      >
        ✦
      </Fab>

      <Panel $open={open}>
        <PanelHeader>
          <HeaderTitle>
            <PulseDot />
            Template Assistant
          </HeaderTitle>
          <CloseBtn onClick={() => setOpen(false)}>✕</CloseBtn>
        </PanelHeader>

        <Messages>
          {messages.map(msg => {
            if (msg.role === 'confirm') {
              if (msg.resolved) {
                return (
                  <ConfirmCard key={msg.id}>
                    <ConfirmText
                      style={{ color: '#8c8c8c', fontStyle: 'italic' }}
                    >
                      {msg.resolved === 'applied' ? '✓ Applied' : '✕ Discarded'}
                    </ConfirmText>
                  </ConfirmCard>
                );
              }
              return (
                <ConfirmCard key={msg.id}>
                  <ConfirmText>{msg.content}</ConfirmText>
                  <ConfirmBtns>
                    <ApplyBtn onClick={() => handleApply(msg.id, msg.changes)}>
                      Apply
                    </ApplyBtn>
                    <DiscardBtn onClick={() => handleDiscard(msg.id)}>
                      Discard
                    </DiscardBtn>
                  </ConfirmBtns>
                </ConfirmCard>
              );
            }
            return (
              <Msg key={msg.id} $role={msg.role}>
                {msg.content}
              </Msg>
            );
          })}
          <TypingDots $visible={pending}>
            <Dot />
            <Dot />
            <Dot />
          </TypingDots>
          <div ref={msgsEndRef} />
        </Messages>

        <InputRow>
          <Input.TextArea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder='Ask me to change the template…'
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={pending}
            style={{
              borderRadius: 20,
              fontSize: 12,
              padding: '7px 13px',
              resize: 'none',
              flex: 1
            }}
          />
          <SendBtn onClick={handleSend} disabled={pending || !input.trim()}>
            ➤
          </SendBtn>
        </InputRow>
      </Panel>
    </>
  );
};

export default AIChatPanel;
